import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AXES,
  CRITERES_ELIMINATOIRES,
  JUSTIFICATION_MINIMUM,
  LIBELLES_AXES,
  PASTILLE,
  estEtoile,
  RUBRIQUES_TRAME,
  calculerNote,
  heureDeSaisie,
  sommePoids,
  type Axe,
  type BaremeApplicable,
  type CandidatureDetail,
  type CharteSigneeResume,
  type ContactDetail,
  type Scores,
} from '@rh/shared';
import { Etoile } from '../composants/Etoile';
import { ErreurReseau, appel } from '../api';
import { useAuth } from '../auth/contexte-auth';
import { LigneEtatSaisie } from '../composants/BandeauEnvoi';
import { Chargement, Erreur } from '../composants/Etat';
import { ResumeEvaluation } from '../composants/HistoriqueContacts';
import { Pastille } from '../composants/Pastille';
import { RefusEnvoi } from '../composants/RefusEnvoi';
import { SaisieMiseEnFile } from '../composants/SaisieMiseEnFile';
import { nomComplet, versChampLocal } from '../format';
import { useFile } from '../horsligne/contexte-file';
import { maintenantServeur } from '../horsligne/horloge';
import { memoire, type FicheEnMemoire, type LieuEnMemoire } from '../horsligne/memoire';
import { heureDeTunis, libelleContact, modeRefus, noteEnFrancais, type ModeRefus } from '../horsligne/textes';
import type {
  DemandeMiseEnFile,
  EnvoiContact,
  RefContact,
  SaisieAppelLocale,
  SaisieEntretienLocale,
  SaisieLocale,
} from '../horsligne/types';
import { useBrouillon, type EtatBrouillon } from '../horsligne/useBrouillon';
import {
  MESSAGE_BAREME_NON_CHARGE,
  validerClotureAppel,
  validerEnregistrementAppel,
  validerEvaluation,
  validerHeure,
  validerTrame,
} from '../horsligne/validation';

/** Grilles des deux axes déclaratifs (docs/BAREME.md § Grille de notation). */
const AIDES_AXES: Partial<Record<Axe, string>> = {
  tabac:
    '5 non-fumeur ou arrêt depuis plus d’un an · 4 occasionnel, tient 4 h · 3 régulier, plages fixes, ' +
    'sans contact client · 2 idem en zone sensible · 1 ne tient pas 4 h',
  alcool:
    '5 abstinence, contrôle accepté · 3 occasionnel hors service, 12 h avant un poste de nuit, ' +
    'contrôle accepté · 0 refus du contrôle',
};

/** Les boutons n'attendent jamais le réseau plus longtemps (docs/UI.md § Écran 3, mode hors ligne). */
const ATTENTE_ISSUE_MS = 8_000;
const MESSAGE_HORS_LIGNE_ACTIONS =
  'Hors ligne : Enregistrer, Clôturer et Valider partent tout seuls au retour du réseau, avec l’heure de votre saisie.';
const MESSAGE_LIEUX_HORS_LIGNE =
  'Liste des lieux indisponible hors ligne : notez le lieu dans les observations, ou choisissez-le au retour du réseau.';
const MESSAGE_CHARTE_HORS_LIGNE =
  'Tabac et alcool : charte signée non vérifiée (hors ligne). Saisissez-les à la main ou attendez le réseau.';

type Source = 'chargement' | 'serveur' | 'memoire' | 'local' | 'indisponible' | 'erreur';

function ficheDe(detail: CandidatureDetail): FicheEnMemoire {
  return {
    id: detail.id,
    prenom: detail.prenom,
    nom: detail.nom,
    telephone: detail.telephone,
    annonceIntitule: detail.annonceIntitule ?? null,
    potentiel: detail.potentiel,
    anonymiseeLe: detail.anonymiseeLe,
  };
}

function saisieAppelDe(contact: ContactDetail | null): SaisieAppelLocale {
  return {
    type: 'telephonique',
    joignable: contact?.joignable ?? null,
    disponible: contact?.disponibleDeSuite ?? null,
    interesse: contact?.interesse ?? null,
    potentiel: contact?.potentiel ?? null,
    rdv: versChampLocal(contact?.rdvLe ?? null),
    lieu: contact?.rdvLieuId ?? '',
    rappel: versChampLocal(contact?.rappelLe ?? null),
    observations: contact?.observations ?? '',
  };
}

function saisieEntretienDe(contact: ContactDetail | null): SaisieEntretienLocale {
  return {
    type: 'presentiel',
    reponses: contact?.reponses ?? {},
    observations: contact?.observations ?? '',
    scores: {},
    eliminatoire: false,
    motif: '',
    precision: '',
    forcer: false,
    noteForcee: '',
    justification: '',
    bareme: null,
  };
}

/** Réseau revenu : relance un chargement resté en échec. */
function useRetourReseau(action: () => void, actif: boolean): void {
  const actionRef = useRef(action);
  actionRef.current = action;
  useEffect(() => {
    if (!actif) return;
    const surRetour = () => actionRef.current();
    window.addEventListener('online', surRetour);
    return () => window.removeEventListener('online', surRetour);
  }, [actif]);
}

/** Écran 3 — panneau de contact (docs/UI.md) : grandes cibles tactiles, peu de clavier. */
export function PanneauContact() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const file = useFile();
  const { instantane, stockage } = file;
  const [contact, setContact] = useState<ContactDetail | null>(null);
  const [fiche, setFiche] = useState<FicheEnMemoire | null>(null);
  const [refLocale, setRefLocale] = useState<RefContact | null>(null);
  const [source, setSource] = useState<Source>('chargement');
  const [erreur, setErreur] = useState<string | null>(null);
  const [essai, setEssai] = useState(0);
  const [envoi, setEnvoi] = useState<EnvoiContact | undefined>(undefined);
  /** Remontages du formulaire demandés par une relecture (abandon d'une saisie refusée). */
  const [generation, setGeneration] = useState(0);
  const remonterApresLecture = useRef(false);
  const utilisateurId = utilisateur?.id ?? null;
  const stockagePret = stockage !== null;

  useEffect(() => {
    let vivant = true;
    setErreur(null);
    /** Relecture demandée (abandon) : le formulaire remonte sur le contact qui vient d'être lu. */
    const remonter = () => {
      if (!remonterApresLecture.current) return;
      remonterApresLecture.current = false;
      setGeneration((g) => g + 1);
    };
    void (async () => {
      let lu: ContactDetail;
      try {
        lu = await appel<ContactDetail>(`/contacts/${id}`);
      } catch (e) {
        if (!vivant) return;
        if (!(e instanceof ErreurReseau)) {
          setErreur(e instanceof Error ? e.message : 'Contact introuvable');
          setSource('erreur');
          return;
        }
        // Coupure : la dernière lecture de l'onglet, sinon la saisie gardée sur le téléphone.
        const connu = memoire.contact(id);
        if (connu) {
          setContact(connu);
          remonter();
          setFiche(memoire.fiche(connu.candidatureId) ?? null);
          setSource('memoire');
          return;
        }
        const cle = utilisateurId ? ([utilisateurId, id] as const) : null;
        const [envoiLocal, brouillon] = cle && stockage
          ? await Promise.all([stockage.lire(cle).catch(() => undefined), stockage.lireBrouillon(cle).catch(() => undefined)])
          : [undefined, undefined];
        if (!vivant) return;
        const trace = envoiLocal ?? brouillon;
        if (trace) {
          setRefLocale({ id, candidatureId: trace.candidatureId, type: trace.type, debuteLe: trace.debuteLe });
          setFiche(memoire.fiche(trace.candidatureId) ?? null);
          setSource('local');
        } else {
          setSource('indisponible');
        }
        return;
      }
      if (!vivant) return;
      memoire.retenirContact(lu);
      setContact(lu);
      remonter();
      setFiche(memoire.fiche(lu.candidatureId) ?? null);
      try {
        const detail = await appel<CandidatureDetail>(`/candidatures/${lu.candidatureId}`);
        if (!vivant) return;
        const lue = ficheDe(detail);
        memoire.retenirFiche(lue);
        setFiche(lue);
        setSource('serveur');
      } catch (e) {
        if (!vivant) return;
        if (e instanceof ErreurReseau) setSource('memoire');
        else {
          setErreur(e instanceof Error ? e.message : 'Fiche introuvable');
          setSource('erreur');
        }
      }
    })();
    return () => {
      vivant = false;
    };
    // `stockage` n'est lu qu'en repli : son ouverture relance la lecture une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, essai, utilisateurId, stockagePret]);

  useRetourReseau(() => setEssai((n) => n + 1), source === 'memoire' || source === 'local' || source === 'indisponible');

  // La saisie de ce contact dans la file : relue à chaque changement de la file.
  const { resumes } = instantane;
  useEffect(() => {
    let vivant = true;
    void file.lire(id).then((lu) => {
      if (vivant) setEnvoi(lu);
    });
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, resumes, stockagePret]);

  // Envoyé par la file : le contact du serveur remplace l'affichage. Refusé : la saisie refusée
  // se relit aussitôt (un contenu effacé quitte l'écran sans attendre le recensement).
  const { ecouter, diffusion, lire } = file;
  useEffect(() => {
    const arreterMoteur = ecouter((evenement) => {
      if (evenement.type === 'envoye' && evenement.contactId === id && evenement.contact) {
        memoire.retenirContact(evenement.contact);
        setContact(evenement.contact);
      } else if (evenement.type === 'refuse' && evenement.contactId === id) {
        void lire(id).then(setEnvoi);
      }
    });
    // Envoi fait par un autre onglet (ou l'application installée) : la saisie en file et le
    // contact se relisent, pour ne pas rouvrir un formulaire sur un contact déjà clos.
    const arreterOnglets = diffusion.ecouter((message) => {
      if (message.type !== 'envoi-termine' || message.contactId !== id || message.utilisateurId !== utilisateurId) return;
      void lire(id).then(setEnvoi);
      if (message.issue === 'envoye') setEssai((n) => n + 1);
    });
    return () => {
      arreterMoteur();
      arreterOnglets();
    };
  }, [ecouter, diffusion, lire, id, utilisateurId]);

  if (source === 'erreur' && erreur) return <Erreur message={erreur} />;
  if (source === 'chargement' || !utilisateurId) return <Chargement quoi="du contact" />;
  if (source === 'indisponible') {
    return (
      <article className="panneau">
        <p className="signal">Contact indisponible hors ligne.</p>
        <div className="actions">
          <button type="button" className="bouton" onClick={() => setEssai((n) => n + 1)}>
            Réessayer
          </button>
        </div>
      </article>
    );
  }

  const ref: RefContact | null = contact
    ? { id: contact.id, candidatureId: contact.candidatureId, type: contact.type, debuteLe: contact.debuteLe }
    : refLocale;
  if (!ref) return <Chargement quoi="du contact" />;

  // Une saisie gardée sur ce téléphone appartient au compte connecté : il en est l'auteur.
  const estAuteur = contact
    ? contact.auteurId === utilisateurId || utilisateur?.role === 'super_admin'
    : true;
  const modifiableServeur = contact ? estAuteur && contact.termineLe === null : true;
  const props: PropsSaisie = {
    refContact: ref,
    contact,
    utilisateurId,
    estAuteur,
    modifiableServeur,
    envoi,
    enLigne: instantane.enLigne,
    onEnvoi: setEnvoi,
    onRelire: () => {
      remonterApresLecture.current = true;
      setEssai((n) => n + 1);
    },
    onOuvrirFiche: () => navigate(`/candidatures/${ref.candidatureId}`),
    onFicheDisparue: (code) => navigate(code === 'FICHE_ANONYMISEE' ? `/candidatures/${ref.candidatureId}` : '/annonces'),
  };
  // Remonté après une relecture demandée : le formulaire repart du contact relu.
  const cleSaisie = `${utilisateurId}:${ref.id}:${generation}`;

  return (
    <article className="panneau">
      <header className="bandeau">
        <Pastille valeur={fiche?.potentiel ?? null} />
        <div>
          <h2>
            {fiche
              ? nomComplet({ prenom: fiche.prenom, nom: fiche.nom, anonymisee: fiche.anonymiseeLe !== null })
              : `${libelleContact(ref.type, ref.debuteLe)} · nom affiché au retour du réseau`}
          </h2>
          <p className="meta">
            <Link to={`/candidatures/${ref.candidatureId}`}>← Fiche</Link>
            {fiche?.annonceIntitule && <span>{fiche.annonceIntitule}</span>}
          </p>
        </div>
        {fiche?.telephone && (
          <a className="appeler" href={`tel:${fiche.telephone}`}>
            {fiche.telephone}
          </a>
        )}
      </header>

      {source === 'memoire' && <p className="mention">Hors ligne · affichage de la dernière lecture</p>}
      {contact && !estAuteur && <p className="signal">Contact de {contact.auteur} : consultation seule.</p>}
      {contact && estAuteur && contact.termineLe && <p className="signal">Contact clôturé : consultation seule.</p>}

      {ref.type === 'telephonique' ? (
        <SaisieAppel key={cleSaisie} {...props} />
      ) : (
        <SaisieEntretien key={cleSaisie} {...props} />
      )}
    </article>
  );
}

interface PropsSaisie {
  refContact: RefContact;
  /** Contact du serveur (ou de la dernière lecture) ; null quand seule la saisie locale est connue. */
  contact: ContactDetail | null;
  utilisateurId: string;
  estAuteur: boolean;
  modifiableServeur: boolean;
  envoi: EnvoiContact | undefined;
  enLigne: boolean;
  /** La file vient de changer pour ce contact (mise en file, abandon). */
  onEnvoi: (envoi: EnvoiContact | undefined) => void;
  /** Relit le contact du serveur, puis remonte le formulaire sur lui (après un abandon). */
  onRelire: () => void;
  onOuvrirFiche: () => void;
  onFicheDisparue: (code: string | null) => void;
}

function OuiNon({
  libelle,
  valeur,
  onChange,
}: {
  libelle: string;
  valeur: boolean | null;
  onChange: (valeur: boolean | null) => void;
}) {
  return (
    <fieldset>
      <legend>{libelle}</legend>
      <div className="bascule">
        {([true, false] as const).map((choix) => (
          <button
            key={String(choix)}
            type="button"
            aria-pressed={valeur === choix}
            className={valeur === choix ? 'actif' : ''}
            onClick={() => onChange(valeur === choix ? null : choix)}
          >
            {choix ? 'Oui' : 'Non'}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Une seule voie d'écriture, en ligne comme hors ligne : écriture du brouillon,
 * mise en file, puis attente de l'issue au plus 8 s, sans jamais bloquer les
 * boutons au-delà de l'écriture locale.
 */
function useEnvoiSaisie<S extends SaisieLocale>(props: PropsSaisie, brouillon: EtatBrouillon<S>) {
  const file = useFile();
  const { refContact, envoi, onEnvoi, onOuvrirFiche } = props;
  const [occupe, setOccupe] = useState(false);
  const [attente, setAttente] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [enregistreLe, setEnregistreLe] = useState<number | null>(null);
  const [finEnvoyee, setFinEnvoyee] = useState<{ envoi: EnvoiContact; le: number } | null>(null);
  const attenteRef = useRef(false);
  /**
   * Clôture ou évaluation en attente pour ce contact : un envoi qui arrive ensuite
   * est le sien. Un refus, ou un simple enregistrement mis en file, l'oublie ;
   * l'élément disparu (envoyé) la laisse en place pour l'événement qui suit.
   */
  const derniereFin = useRef<EnvoiContact | null>(null);
  if (envoi !== undefined) derniereFin.current = envoi.etat === 'en_attente' && envoi.fin && envoi.saisie ? envoi : null;

  // Envoi fait plus tard, écran ouvert : le résumé passe en direct à « envoyé ».
  const { ecouter, diffusion } = file;
  const { utilisateurId } = props;
  useEffect(() => {
    const surEnvoi = () => {
      if (derniereFin.current) setFinEnvoyee({ envoi: derniereFin.current, le: maintenantServeur() });
      else setEnregistreLe(maintenantServeur());
    };
    const arreterMoteur = ecouter((evenement) => {
      // En attente d'issue, c'est `mettreEnFile` qui conclut (fiche ou ligne d'état).
      if (evenement.type === 'envoye' && evenement.contactId === refContact.id && !attenteRef.current) surEnvoi();
    });
    // Envoyé par un autre onglet, ou par l'application installée : cet onglet n'en voit que l'annonce.
    const arreterOnglets = diffusion.ecouter((message) => {
      if (
        message.type === 'envoi-termine' &&
        message.issue === 'envoye' &&
        message.contactId === refContact.id &&
        message.utilisateurId === utilisateurId
      ) {
        surEnvoi();
      }
    });
    return () => {
      arreterMoteur();
      arreterOnglets();
    };
  }, [ecouter, diffusion, refContact.id, utilisateurId]);

  async function mettreEnFile(construire: (brouillonRevision: number) => DemandeMiseEnFile): Promise<void> {
    setMessage(null);
    setOccupe(true);
    let demande: DemandeMiseEnFile;
    try {
      await brouillon.ecrireMaintenant();
      demande = construire(brouillon.revision);
      const resultat = await file.mettreEnFile(demande);
      if (!resultat.ok) {
        setMessage(resultat.message);
        return;
      }
      brouillon.noterMiseEnFile(demande.brouillonRevision ?? brouillon.revision);
      onEnvoi(resultat.envoi);
      derniereFin.current = resultat.envoi.fin ? resultat.envoi : null;
    } finally {
      setOccupe(false);
    }
    attenteRef.current = true;
    setAttente(true);
    const issue = await file.attendreContact(refContact.id, ATTENTE_ISSUE_MS);
    attenteRef.current = false;
    setAttente(false);
    if (issue !== 'envoye') return;
    if (demande.genre === 'enregistrement') setEnregistreLe(maintenantServeur());
    else onOuvrirFiche();
  }

  return { occupe, attente, message, setMessage, enregistreLe, finEnvoyee, mettreEnFile };
}

/** Clôture ou évaluation en file, ou déjà envoyée : le formulaire laisse la place au résumé. */
function EtatFin({
  props,
  attente,
  finEnvoyee,
}: {
  props: PropsSaisie;
  attente: boolean;
  finEnvoyee: { envoi: EnvoiContact; le: number } | null;
}) {
  const { envoi, enLigne, onOuvrirFiche } = props;
  if (finEnvoyee) {
    return <SaisieMiseEnFile envoi={finEnvoyee.envoi} enLigne={enLigne} envoyeLe={finEnvoyee.le} onOuvrirFiche={onOuvrirFiche} />;
  }
  if (!envoi || envoi.etat !== 'en_attente' || !envoi.fin) return null;
  if (attente && enLigne) {
    return (
      <p className="saisie-en-file" role="status">
        Envoi en cours…
      </p>
    );
  }
  return <SaisieMiseEnFile envoi={envoi} enLigne={enLigne} onOuvrirFiche={onOuvrirFiche} />;
}

function finEnFile(props: PropsSaisie, finEnvoyee: unknown): boolean {
  return finEnvoyee !== null || (props.envoi?.etat === 'en_attente' && props.envoi.fin !== null);
}

function modeDuRefus(envoi: EnvoiContact | undefined): ModeRefus | null {
  return envoi?.etat === 'refuse' && envoi.refus ? modeRefus(envoi.refus) : null;
}

/** Mentions communes sous l'en-tête du formulaire. */
function MentionsSaisie({ brouillon }: { brouillon: Pick<EtatBrouillon<SaisieLocale>, 'restaure' | 'autreOnglet'> }) {
  return (
    <>
      {brouillon.restaure && (
        <p className="mention">Saisie reprise de ce téléphone ({heureDeTunis(brouillon.restaure.modifieLe)}).</p>
      )}
      {brouillon.autreOnglet && (
        <p className="signal">Ce contact est aussi ouvert dans un autre onglet : la dernière saisie l’emporte.</p>
      )}
    </>
  );
}

/** Refus : abandon, « J’ai compris », renvoi daté de l'envoi — communs aux deux formulaires. */
function useGestesRefus<S extends SaisieLocale>(props: PropsSaisie, brouillon: EtatBrouillon<S>) {
  const file = useFile();
  const { refContact, envoi, onEnvoi, onFicheDisparue, onRelire } = props;
  // Contenu effacé du téléphone (fiche anonymisée ou supprimée) : il quitte aussi l'écran et la
  // mémoire, et une écriture de brouillon encore attendue est annulée.
  const efface = modeDuRefus(envoi) === 'efface';
  const { vider } = brouillon;
  useEffect(() => {
    if (efface) void vider();
  }, [efface, vider]);
  return {
    abandonner: async () => {
      await file.abandonner(refContact.id);
      await brouillon.vider();
      onEnvoi(undefined);
      // Le formulaire repart du serveur : le contact en mémoire a pu être clos entre-temps.
      onRelire();
    },
    compris: async () => {
      const code = envoi?.refus?.code ?? null;
      await file.abandonner(refContact.id);
      await brouillon.vider();
      onEnvoi(undefined);
      onFicheDisparue(code);
    },
    envoyerEvaluationSeule: () => void file.envoyerEvaluationSeule(refContact.id),
  };
}

function SaisieAppel(props: PropsSaisie) {
  const { refContact, contact, utilisateurId, modifiableServeur, envoi, enLigne } = props;
  const [lieux, setLieux] = useState<ReadonlyArray<LieuEnMemoire> | null>(() => memoire.lieux() ?? null);
  const [lieuxHorsLigne, setLieuxHorsLigne] = useState(false);
  const [essaiLieux, setEssaiLieux] = useState(0);
  const mode = modeDuRefus(envoi);
  const lectureSeule = mode === 'clos' || mode === 'bloque' || mode === 'efface';
  const enFile = envoi?.etat === 'en_attente' && envoi.fin !== null;
  const modifiable = modifiableServeur && !enFile && !lectureSeule;

  const brouillon = useBrouillon<SaisieAppelLocale>({
    utilisateurId,
    contact: refContact,
    modifiable,
    initiale: () => saisieAppelDe(contact),
  });
  const envoiSaisie = useEnvoiSaisie(props, brouillon);
  const gestes = useGestesRefus(props, brouillon);
  const { saisie } = brouillon;

  useEffect(() => {
    let vivant = true;
    appel<LieuEnMemoire[]>('/points-de-vente')
      .then((lus) => {
        if (!vivant) return;
        memoire.retenirLieux(lus);
        setLieux(lus);
        setLieuxHorsLigne(false);
      })
      .catch((e: unknown) => {
        if (!vivant) return;
        const connus = memoire.lieux();
        setLieux(connus ?? null);
        setLieuxHorsLigne(e instanceof ErreurReseau && !connus);
      });
    return () => {
      vivant = false;
    };
  }, [essaiLieux]);
  useRetourReseau(() => setEssaiLieux((n) => n + 1), lieuxHorsLigne);

  /** Lieux chargés, plus celui déjà enregistré (un lieu désactivé depuis reste valable s'il ne change pas). */
  const lieuxConnus = lieux ? [...lieux.map((l) => l.id), ...(contact?.rdvLieuId ? [contact.rdvLieuId] : [])] : null;
  const fuseau = Intl.DateTimeFormat().resolvedOptions().timeZone;

  function enregistrer(): void {
    const issue = validerEnregistrementAppel(saisie, lieuxConnus);
    if (!issue.ok) {
      envoiSaisie.setMessage(issue.message);
      return;
    }
    void envoiSaisie.mettreEnFile((brouillonRevision) => ({
      genre: 'enregistrement',
      utilisateurId,
      contact: refContact,
      saisie,
      corps: issue.valeur,
      brouillonRevision,
    }));
  }

  function cloturer(): void {
    const issue = validerClotureAppel(saisie, lieuxConnus);
    if (!issue.ok) {
      envoiSaisie.setMessage(issue.message);
      return;
    }
    void envoiSaisie.mettreEnFile((brouillonRevision) => ({
      genre: 'cloture',
      utilisateurId,
      contact: refContact,
      saisie,
      corps: issue.valeur,
      termineLe: heureDeSaisie(refContact.debuteLe, maintenantServeur()),
      brouillonRevision,
    }));
  }

  if (finEnFile(props, envoiSaisie.finEnvoyee)) {
    return (
      <section className="saisie">
        <EtatFin props={props} attente={envoiSaisie.attente} finEnvoyee={envoiSaisie.finEnvoyee} />
      </section>
    );
  }
  if (mode === 'efface' && envoi) {
    // Fiche anonymisée ou supprimée : le contenu a quitté le téléphone, il ne reste que le refus.
    return (
      <section className="saisie">
        <RefusEnvoi
          envoi={envoi}
          enLigne={enLigne}
          onRenvoyerHeureEnvoi={cloturer}
          onEnvoyerEvaluationSeule={gestes.envoyerEvaluationSeule}
          onAbandonner={() => void gestes.abandonner()}
          onCompris={() => void gestes.compris()}
        />
      </section>
    );
  }
  if (!brouillon.pret) return <Chargement quoi="de la saisie" />;

  const lieuxAffiches = [...(lieux ?? [])];
  if (contact?.rdvLieuId && !lieuxAffiches.some((l) => l.id === contact.rdvLieuId)) {
    lieuxAffiches.push({ id: contact.rdvLieuId, libelle: contact.rdvLieu ?? 'Lieu enregistré' });
  }

  return (
    <section className="saisie">
      {envoi?.etat === 'refuse' && (
        <RefusEnvoi
          envoi={envoi}
          enLigne={enLigne}
          onRenvoyerHeureEnvoi={cloturer}
          onEnvoyerEvaluationSeule={gestes.envoyerEvaluationSeule}
          onAbandonner={() => void gestes.abandonner()}
          onCompris={() => void gestes.compris()}
        />
      )}
      <h3>Appel téléphonique</h3>
      <LigneEtatSaisie
        contactId={refContact.id}
        brouillonNonEnregistre={brouillon.nonEnregistre}
        enregistreLe={envoiSaisie.enregistreLe}
      />
      <MentionsSaisie brouillon={brouillon} />
      <fieldset className="saisie" disabled={!modifiable || envoiSaisie.occupe}>
        <OuiNon
          libelle="Candidat joint"
          valeur={saisie.joignable}
          onChange={(joignable) => brouillon.modifier({ joignable })}
        />

        {saisie.joignable === true && (
          <>
            <OuiNon
              libelle="Disponible de suite"
              valeur={saisie.disponible}
              onChange={(disponible) => brouillon.modifier({ disponible })}
            />
            <OuiNon
              libelle="Intéressé par le poste"
              valeur={saisie.interesse}
              onChange={(interesse) => brouillon.modifier({ interesse })}
            />

            <fieldset className="choix-pastille">
              <legend>Pastille de potentiel</legend>
              <div className="grille">
                {PASTILLE.filter((niveau) => niveau.valeur !== null).map((niveau) => (
                  <button
                    key={niveau.valeur}
                    type="button"
                    aria-pressed={saisie.potentiel === niveau.valeur}
                    className={saisie.potentiel === niveau.valeur ? 'actif' : ''}
                    style={{ '--teinte': niveau.couleur } as CSSProperties}
                    onClick={() => brouillon.modifier({ potentiel: niveau.valeur })}
                  >
                    <strong>{niveau.valeur}</strong>
                    <span>
                      {niveau.libelle}
                      <Etoile potentiel={niveau.valeur} />
                    </span>
                  </button>
                ))}
              </div>
              {estEtoile(saisie.potentiel) && (
                <p className="mention etoile-posee">
                  <Etoile potentiel={saisie.potentiel} /> Pastille 5 : le candidat est étoilé, potentiel élevé.
                </p>
              )}
            </fieldset>

            <fieldset className="groupe">
              <legend>Rendez-vous</legend>
              <div className="ligne">
                <input
                  type="datetime-local"
                  value={saisie.rdv}
                  aria-label="Date et heure du rendez-vous"
                  onChange={(evenement) => brouillon.modifier({ rdv: evenement.target.value })}
                />
                <select
                  value={saisie.lieu}
                  aria-label="Lieu du rendez-vous"
                  onChange={(evenement) => brouillon.modifier({ lieu: evenement.target.value })}
                >
                  <option value="">Lieu…</option>
                  {lieuxAffiches.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.libelle}
                    </option>
                  ))}
                </select>
              </div>
              {lieuxHorsLigne && <p className="mention">{MESSAGE_LIEUX_HORS_LIGNE}</p>}
              {fuseau && fuseau !== 'Africa/Tunis' && (
                <p className="mention">
                  Le téléphone est réglé sur le fuseau {fuseau} : les heures saisies suivent ce fuseau.
                </p>
              )}
            </fieldset>
          </>
        )}

        <label className="champ">
          Rappeler le
          <input
            type="datetime-local"
            value={saisie.rappel}
            onChange={(evenement) => brouillon.modifier({ rappel: evenement.target.value })}
          />
        </label>
        <label className="champ">
          Observations
          <textarea
            rows={4}
            maxLength={4000}
            value={saisie.observations}
            onChange={(evenement) => brouillon.modifier({ observations: evenement.target.value })}
          />
        </label>
      </fieldset>

      {envoiSaisie.message && <Erreur message={envoiSaisie.message} />}
      {modifiable && (
        <div className="actions">
          <button type="button" className="bouton-second" disabled={envoiSaisie.occupe} onClick={enregistrer}>
            Enregistrer
          </button>
          <button type="button" className="bouton" disabled={envoiSaisie.occupe} onClick={cloturer}>
            Clôturer l’appel
          </button>
        </div>
      )}
      {modifiable && !enLigne && <p className="mention">{MESSAGE_HORS_LIGNE_ACTIONS}</p>}
    </section>
  );
}

function SaisieEntretien(props: PropsSaisie) {
  const { refContact, contact, utilisateurId, estAuteur, modifiableServeur, envoi, enLigne } = props;
  const candidatureId = refContact.candidatureId;
  const [bareme, setBareme] = useState<BaremeApplicable | null>(() => memoire.bareme(candidatureId) ?? null);
  const [baremeHorsLigne, setBaremeHorsLigne] = useState(false);
  const [erreurBareme, setErreurBareme] = useState<string | null>(null);
  const [essaiBareme, setEssaiBareme] = useState(0);
  const [repriseCharte, setRepriseCharte] = useState<CharteSigneeResume | null>(null);
  const [charteHorsLigne, setCharteHorsLigne] = useState(false);
  const [essaiCharte, setEssaiCharte] = useState(0);
  const charteAppliquee = useRef(false);
  const mode = modeDuRefus(envoi);
  const lectureSeule = mode === 'clos' || mode === 'bloque' || mode === 'efface';
  const enFile = envoi?.etat === 'en_attente' && envoi.fin !== null;
  const modifiable = modifiableServeur && !enFile && !lectureSeule;
  const dejaEvalue = contact !== null && contact.evaluation !== null;
  // Un entretien clos sans évaluation s'évalue encore : la notation a sa propre règle.
  const notationModifiable = estAuteur && !dejaEvalue && !enFile && !lectureSeule;

  const brouillon = useBrouillon<SaisieEntretienLocale>({
    utilisateurId,
    contact: refContact,
    modifiable: modifiable || notationModifiable,
    initiale: () => saisieEntretienDe(contact),
  });
  const envoiSaisie = useEnvoiSaisie(props, brouillon);
  const gestes = useGestesRefus(props, brouillon);
  const { saisie, initialiser } = brouillon;

  useEffect(() => {
    let vivant = true;
    appel<BaremeApplicable>(`/candidatures/${candidatureId}/bareme`)
      .then((lu) => {
        if (!vivant) return;
        memoire.retenirBareme(candidatureId, lu);
        setBareme(lu);
        setBaremeHorsLigne(false);
        setErreurBareme(null);
      })
      .catch((e: unknown) => {
        if (!vivant) return;
        if (e instanceof ErreurReseau) {
          const connu = memoire.bareme(candidatureId);
          setBareme(connu ?? null);
          setBaremeHorsLigne(!connu);
        } else {
          setErreurBareme(e instanceof Error ? e.message : 'Barème introuvable');
        }
      });
    return () => {
      vivant = false;
    };
  }, [candidatureId, essaiBareme]);
  useRetourReseau(() => setEssaiBareme((n) => n + 1), baremeHorsLigne);

  // Barème changé depuis l'entretien : on relit celui qui s'applique désormais.
  const refusLe = envoi?.refus?.le ?? null;
  useEffect(() => {
    if (mode === 'bareme') setEssaiBareme((n) => n + 1);
  }, [mode, refusLe]);

  /**
   * La charte signée est la source des axes tabac et alcool (docs/BAREME.md) :
   * ses notes, et un éventuel éliminatoire, pré-remplissent la notation une fois.
   * Ce qui a déjà été saisi garde la main.
   */
  useEffect(() => {
    if (dejaEvalue) return;
    let vivant = true;
    const reprendre = (chartes: CharteSigneeResume[]) => {
      const signee = chartes.find((c) => c.documentScanId && c.lecture);
      const lecture = signee?.lecture;
      if (!signee || !lecture) return;
      setRepriseCharte(signee);
      if (charteAppliquee.current) return;
      charteAppliquee.current = true;
      initialiser((precedente) => {
        const repris: Scores = {};
        if (lecture.scores.tabac !== undefined) repris.tabac = lecture.scores.tabac;
        if (lecture.scores.alcool !== undefined) repris.alcool = lecture.scores.alcool;
        const suivante = { ...precedente, scores: { ...repris, ...precedente.scores } };
        if (lecture.eliminatoire) {
          suivante.eliminatoire = true;
          suivante.motif = lecture.eliminatoire.cle;
          suivante.precision = precedente.precision || lecture.eliminatoire.raison;
        }
        return suivante;
      });
    };
    appel<CharteSigneeResume[]>(`/candidatures/${candidatureId}/chartes`)
      .then((chartes) => {
        if (!vivant) return;
        memoire.retenirChartes(candidatureId, chartes);
        setCharteHorsLigne(false);
        reprendre(chartes);
      })
      .catch((e: unknown) => {
        if (!vivant || !(e instanceof ErreurReseau)) return;
        const connues = memoire.chartes(candidatureId);
        if (connues) reprendre(connues);
        else setCharteHorsLigne(true);
      });
    return () => {
      vivant = false;
    };
  }, [candidatureId, dejaEvalue, essaiCharte, initialiser]);
  useRetourReseau(() => setEssaiCharte((n) => n + 1), charteHorsLigne);

  const noteCalculee = useMemo(
    () => (bareme ? calculerNote(saisie.scores, bareme.poids) : 0),
    [bareme, saisie.scores],
  );
  const total = bareme ? sommePoids(bareme.poids) : 0;

  const consigneBareme = useMemo(() => {
    const refuse = envoi?.refus?.bareme;
    if (mode !== 'bareme' || !bareme || !refuse || bareme.id === refuse.affiche.id) return null;
    const ancienne = envoi?.fin?.genre === 'evaluation' ? envoi.fin.noteAffichee : null;
    const nouvelle = saisie.eliminatoire ? 0 : noteCalculee;
    const note =
      ancienne !== null
        ? `la note passe de ${noteEnFrancais(ancienne)} à ${noteEnFrancais(nouvelle)}.`
        : `la note calculée est ${noteEnFrancais(nouvelle)}.`;
    const forcage = saisie.forcer && !saisie.eliminatoire ? ' La note forcée est à revoir.' : '';
    return `Le barème v${bareme.version} s’affiche maintenant. Vos scores sont conservés : ${note} Vérifiez-la, puis validez à nouveau.${forcage}`;
  }, [mode, bareme, envoi, saisie.eliminatoire, saisie.forcer, noteCalculee]);

  function noter(axe: Axe, valeur: number | null): void {
    brouillon.modifier((precedente) => {
      const scores = { ...precedente.scores };
      if (valeur === null) delete scores[axe];
      else scores[axe] = valeur;
      return { ...precedente, scores };
    });
  }

  function enregistrerTrame(): void {
    const issue = validerTrame(saisie);
    if (!issue.ok) {
      envoiSaisie.setMessage(issue.message);
      return;
    }
    void envoiSaisie.mettreEnFile((brouillonRevision) => ({
      genre: 'enregistrement',
      utilisateurId,
      contact: refContact,
      saisie,
      corps: issue.valeur,
      brouillonRevision,
    }));
  }

  function valider(): void {
    const issue = validerEvaluation(saisie, bareme);
    if (!issue.ok) {
      envoiSaisie.setMessage(issue.message);
      return;
    }
    let evalueeLe = heureDeSaisie(refContact.debuteLe, maintenantServeur());
    // Revue après un barème changé : l'évaluation garde l'heure de l'entretien d'origine.
    if (mode === 'bareme' && envoi?.fin?.genre === 'evaluation') {
      const origine = validerHeure(envoi.fin.evalueeLe, refContact.debuteLe, maintenantServeur());
      if (!origine.ok) {
        envoiSaisie.setMessage(origine.message);
        return;
      }
      evalueeLe = origine.valeur;
    }
    const question = enLigne
      ? 'L’évaluation sera figée et ne se modifiera plus. La valider ?'
      : `Hors ligne : l’évaluation partira au retour du réseau, datée de ${heureDeTunis(evalueeLe)}, sur le barème ` +
        `v${issue.valeur.bareme.version}. Elle sera figée dès sa réception ; si le barème change d’ici là, elle vous ` +
        'reviendra pour vérification. La valider ?';
    if (!window.confirm(question)) return;
    void envoiSaisie.mettreEnFile((brouillonRevision) => ({
      genre: 'evaluation',
      utilisateurId,
      contact: refContact,
      saisie,
      corps: modifiableServeur ? issue.valeur.corps : null,
      entree: issue.valeur.entree,
      bareme: issue.valeur.bareme,
      noteAffichee: issue.valeur.noteAffichee,
      evalueeLe,
      brouillonRevision,
    }));
  }

  /** Heure du téléphone refusée : la même évaluation repart, datée de l'envoi. */
  function renvoyerHeureEnvoi(): void {
    const fin = envoi?.fin;
    if (fin?.genre !== 'evaluation') return;
    void envoiSaisie.mettreEnFile((brouillonRevision) => ({
      genre: 'evaluation',
      utilisateurId,
      contact: refContact,
      saisie: envoi?.saisie ?? saisie,
      corps: null,
      entree: fin.entree,
      bareme: fin.bareme,
      noteAffichee: fin.noteAffichee,
      evalueeLe: heureDeSaisie(refContact.debuteLe, maintenantServeur()),
      brouillonRevision,
    }));
  }

  if (finEnFile(props, envoiSaisie.finEnvoyee)) {
    return (
      <section className="saisie">
        <EtatFin props={props} attente={envoiSaisie.attente} finEnvoyee={envoiSaisie.finEnvoyee} />
      </section>
    );
  }
  if (mode === 'efface' && envoi) {
    // Fiche anonymisée ou supprimée : le contenu a quitté le téléphone, il ne reste que le refus.
    return (
      <section className="saisie">
        <RefusEnvoi
          envoi={envoi}
          enLigne={enLigne}
          onRenvoyerHeureEnvoi={renvoyerHeureEnvoi}
          onEnvoyerEvaluationSeule={gestes.envoyerEvaluationSeule}
          onAbandonner={() => void gestes.abandonner()}
          onCompris={() => void gestes.compris()}
        />
      </section>
    );
  }
  if (!brouillon.pret) return <Chargement quoi="de la saisie" />;
  const occupe = envoiSaisie.occupe;

  return (
    <section className="saisie">
      {envoi?.etat === 'refuse' && (
        <RefusEnvoi
          envoi={envoi}
          enLigne={enLigne}
          consigneBareme={consigneBareme}
          onRenvoyerHeureEnvoi={renvoyerHeureEnvoi}
          onEnvoyerEvaluationSeule={gestes.envoyerEvaluationSeule}
          onAbandonner={() => void gestes.abandonner()}
          onCompris={() => void gestes.compris()}
        />
      )}
      <h3>Entretien présentiel</h3>
      <LigneEtatSaisie
        contactId={refContact.id}
        brouillonNonEnregistre={brouillon.nonEnregistre}
        enregistreLe={envoiSaisie.enregistreLe}
      />
      <MentionsSaisie brouillon={brouillon} />
      <fieldset className="saisie" disabled={!modifiable || occupe}>
        {RUBRIQUES_TRAME.map((rubrique) => (
          <label key={rubrique.cle} className="champ">
            {rubrique.libelle}
            <small className="mention">{rubrique.aide}</small>
            <textarea
              rows={2}
              maxLength={4000}
              value={saisie.reponses[rubrique.cle] ?? ''}
              onChange={(evenement) => {
                const texte = evenement.target.value;
                brouillon.modifier((s) => ({ ...s, reponses: { ...s.reponses, [rubrique.cle]: texte } }));
              }}
            />
          </label>
        ))}
        <label className="champ">
          Observations
          <textarea
            rows={3}
            maxLength={4000}
            value={saisie.observations}
            onChange={(evenement) => brouillon.modifier({ observations: evenement.target.value })}
          />
        </label>
      </fieldset>
      {modifiable && !dejaEvalue && (
        <div className="actions">
          <button type="button" className="bouton-second" disabled={occupe} onClick={enregistrerTrame}>
            Enregistrer la trame
          </button>
        </div>
      )}

      {contact?.evaluation && (
        <>
          <h3>Évaluation</h3>
          <ResumeEvaluation evaluation={contact.evaluation} />
        </>
      )}

      {erreurBareme && !dejaEvalue && <Erreur message={erreurBareme} />}
      {!dejaEvalue && estAuteur && !bareme && baremeHorsLigne && <p className="signal">{MESSAGE_BAREME_NON_CHARGE}</p>}

      {!dejaEvalue && estAuteur && bareme && (
        <fieldset className="saisie" disabled={!notationModifiable || occupe}>
          <legend>
            Notation — {bareme.familleLibelle}, barème v{bareme.version}
          </legend>
          {!bareme.figeSurAnnonce && (
            <p className="mention">
              Annonce pas encore validée dans l’application : le barème actif du métier s’applique.
            </p>
          )}
          {repriseCharte ? (
            <p className="signal">
              Tabac et alcool repris de la charte signée
              {repriseCharte.signeeLe ? ` le ${repriseCharte.signeeLe.split('-').reverse().join('/')}` : ''}.
              {repriseCharte.lecture && repriseCharte.lecture.aTrancher.length > 0
                ? ` À trancher : ${repriseCharte.lecture.aTrancher.join(' ; ')}.`
                : ''}
            </p>
          ) : charteHorsLigne ? (
            <p className="signal">{MESSAGE_CHARTE_HORS_LIGNE}</p>
          ) : (
            <p className="mention">
              Tabac et alcool : aucune charte signée enregistrée. La scanner depuis la fiche reprend ses cases
              cochées ici.
            </p>
          )}

          <ul className="scores">
            {AXES.map((axe) => (
              <li key={axe} className={bareme.poids[axe] === 0 ? 'axe-nul' : ''}>
                <div className="ligne-axe">
                  <span>{LIBELLES_AXES[axe]}</span>
                  <small className="mention">
                    {total ? Math.round((bareme.poids[axe] / total) * 100) : 0} %
                  </small>
                </div>
                {AIDES_AXES[axe] && <small className="mention">{AIDES_AXES[axe]}</small>}
                <div className="choix-score" role="group" aria-label={LIBELLES_AXES[axe]}>
                  {[null, 0, 1, 2, 3, 4, 5].map((valeur) => {
                    const actif = (saisie.scores[axe] ?? null) === valeur;
                    return (
                      <button
                        key={valeur ?? 'aucun'}
                        type="button"
                        aria-pressed={actif}
                        className={actif ? 'actif' : ''}
                        title={valeur === null ? 'Non interrogé' : undefined}
                        onClick={() => noter(axe, valeur)}
                      >
                        {valeur ?? '—'}
                      </button>
                    );
                  })}
                </div>
              </li>
            ))}
          </ul>

          <label className="case">
            <input
              type="checkbox"
              checked={saisie.eliminatoire}
              onChange={(evenement) => brouillon.modifier({ eliminatoire: evenement.target.checked })}
            />
            Critère éliminatoire constaté
          </label>
          {saisie.eliminatoire && (
            <div className="groupe">
              <select
                value={saisie.motif}
                aria-label="Motif éliminatoire"
                onChange={(evenement) =>
                  brouillon.modifier({ motif: evenement.target.value as SaisieEntretienLocale['motif'] })
                }
              >
                <option value="">Motif…</option>
                {CRITERES_ELIMINATOIRES.filter(
                  (critere) => critere.cle !== 'permis' || bareme.groupe === 'chauffeur',
                ).map((critere) => (
                  <option key={critere.cle} value={critere.cle}>
                    {critere.libelle}
                  </option>
                ))}
              </select>
              <input
                type="text"
                maxLength={1000}
                value={saisie.precision}
                placeholder="Précision (facultative)"
                onChange={(evenement) => brouillon.modifier({ precision: evenement.target.value })}
              />
            </div>
          )}

          <div className="notes-calcul">
            <span>{saisie.eliminatoire ? 'Note forcée à 0 par le critère éliminatoire' : 'Note calculée'}</span>
            <span className="note">
              <strong>{(saisie.eliminatoire ? 0 : noteCalculee).toFixed(1)}</strong>
              <small>/5</small>
            </span>
          </div>

          {!saisie.eliminatoire && (
            <>
              <label className="case">
                <input
                  type="checkbox"
                  checked={saisie.forcer}
                  onChange={(evenement) => brouillon.modifier({ forcer: evenement.target.checked })}
                />
                Forcer la note
              </label>
              {saisie.forcer && (
                <div className="groupe">
                  <input
                    type="number"
                    min={0}
                    max={5}
                    step={0.1}
                    value={saisie.noteForcee}
                    aria-label="Note forcée"
                    placeholder="Note forcée"
                    onChange={(evenement) => brouillon.modifier({ noteForcee: evenement.target.value })}
                  />
                  <label className="champ">
                    Justification ({JUSTIFICATION_MINIMUM} caractères au moins)
                    <textarea
                      rows={2}
                      maxLength={2000}
                      value={saisie.justification}
                      onChange={(evenement) => brouillon.modifier({ justification: evenement.target.value })}
                    />
                  </label>
                </div>
              )}
            </>
          )}

          <div className="actions">
            <button type="button" className="bouton" disabled={occupe} onClick={valider}>
              Valider l’évaluation
            </button>
          </div>
        </fieldset>
      )}

      {envoiSaisie.message && <Erreur message={envoiSaisie.message} />}
      {(modifiable || notationModifiable) && !enLigne && <p className="mention">{MESSAGE_HORS_LIGNE_ACTIONS}</p>}
    </section>
  );
}
